import React from "react";
import { Box, Flex, Heading, Text, Link as ExternalLink, Image } from "theme-ui";
import { Link } from "gatsby";
import styled from '@emotion/styled'
import PropTypes from "prop-types";
import { icons } from 'lucide-react';
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import freeTag from "../../images/free-tag.svg";
import { Button, DisplayIcon, Tag } from '../atoms';
import { colors, typography } from '../../styles/theme';

const renderCardLink = (url, children) => {
  const isExternalLink = url.startsWith("http");
  return (
    <>
      {isExternalLink ? (
        <ExternalLink href={url} target="_blank">
          {children}
        </ExternalLink>
      ) : (
        <Link to={url}>{children}</Link>
      )}
    </>
  );
};

const InfoTileWrapper = styled(Box)`
  position: relative;
  border-radius: 20px;
  padding: 1.5rem;
  a {
    text-decoration: none;
    &:hover {
      text-decoration: none;
    }
  }
  @media(max-width: 360px) {
    max-width: 20rem;
  }
`;

const styles = {
  container: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  tagStyle: {
    position: "absolute",
    top: "2rem",
    right: "2rem",
    zIndex: "1",
  },
  freeTagStyle: {
    position: "absolute",
    top: "2rem",
    left: "0rem",
    width: "5rem",
    height: "3.313rem",
  },
  imageContainer: {
    width: "100%",
    marginBottom: "1.5rem",
  },
  imageStyle: {
    height: "100%",
    width: "100%",
    borderRadius: "20px",
  },
  academyThumbnail: {
    width: "100%",
    alignItems: "flex-end",
    height: "11rem",
    borderRadius: "20px",
    backgroundColor: colors.primary100,
    border: `1px solid ${colors.primaryMain}`,
    padding: "1.313rem",
    marginBottom: "1.5rem",
  },
  titleTextStyle: {
    ...typography.h5,
    display: "inline-block",
    paddingTop: "5.813rem",
  },
  iconStyle: {
    marginBottom: "1.5rem",
  },
  primaryLabelStyle: {
    marginBottom: "0.5rem",
  },
  titleStyle: {
    ...typography.h5,
    fontSize: [typography.h5.fontSize, typography.h5.fontSize, typography.h6.fontSize, typography.h5.fontSize],
    lineHeight: [typography.h5.lineHeight, typography.h5.lineHeight, typography.h6.lineHeight, typography.h5.lineHeight],
    marginBottom: "0.5rem",
    display: "-webkit-box",
    overflow: "hidden",
    "-webkit-line-clamp": "2",
    "-webkit-box-orient": "vertical"
  },
  textStyle: {
    ...typography.textMd400,
    color: colors.neutral900,
    marginBottom: "0.5rem",
  },
  logoContainerStyle: {
    height: "12.5rem", 
    border: `1px solid ${colors.neutral200}`, 
    borderRadius: "1.25rem",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.white
  }
};

const PurpleFilledCircle = styled.div`
  width: 55px;
  height: 55px;
  border-radius: 50%;
  background-color: ${colors.primary1000};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);

  svg {
    fill: white;
    color: white;
    margin-left: 5px;
  }
`;

const {
  container,
  tagStyle,
  freeTagStyle,
  imageContainer,
  imageStyle,
  iconStyle,
  primaryLabelStyle,
  secondaryLabelStyle,
  titleStyle,
  textStyle,
  academyThumbnail,
  titleTextStyle,
  logoContainerStyle
} = styles;

const InfoTile = ({
  variant,
  image,
  imageUrl,
  imageAltText,
  iconName,
  theme,
  title,
  content,
  titleText,
  tagLabel,
  tagColor,
  tagLabels,
  tagColors,
  hasFreeTag,
  topLabel,
  bottomLabel,
  buttonLabel,
  onBtnClick,
  btnWidth,
  btnVariant,
  btnIcon,
  btnIconDirection,
  hasBorder,
  hideHoverBgColor,
  url,
  showPlayButton,
  backgroundColor,
  titleColor,
  contentColor,
  customStyles,
}) => {
  const hasUrl = url && url.length > 0;
  const PlayIcon = icons['Play'];

  const contentBox = (
    <Flex
          sx={container}
        >
          {tagLabel && (
            <Box sx={{ ...tagStyle, maxWidth: "80%" }}>
              <Tag
                label={tagLabel}
                bgColor={tagColor}
                customStyles={{
                  "@media screen and (max-width: 400px)": {
                    ...typography.textSm,
                  },
                }}
              />
            </Box>
          )}
          {(variant === "default" || variant === "webinar") && image && (
            <Box sx={{ width: "100%", position: "relative" }}>
              <GatsbyImage
                image={getImage(image)}
                style={imageContainer}
                imgStyle={imageStyle}
                alt={imageAltText}
              />
              {showPlayButton && variant === "webinar" && (
                <PurpleFilledCircle>
                  <PlayIcon size={28} />
                </PurpleFilledCircle>
              )}
            </Box>
          )}
          {(variant === "logo") && (
              <Flex sx={{
                ...imageContainer,
                ...logoContainerStyle
              }}>
                {image && (
                  <GatsbyImage 
                    image={getImage(image)} 
                    alt={imageAltText} 
                    imgStyle={{...imageStyle, borderRadius: "none"}} 
                  />
                )}
                {imageUrl && <Image src={imageUrl} alt={imageAltText} sx={{...imageStyle, borderRadius: "none"}} />}
              </Flex>
          )}
          {(variant === "default" || variant === "webinar") && imageUrl && (
            <Box sx={{ width: "100%", position: "relative" }}>
              <Box sx={imageContainer} className="url-image">
                <Image src={imageUrl} alt={imageAltText} sx={imageStyle} />
              </Box>
              {showPlayButton && variant === "webinar" && (
                <PurpleFilledCircle>
                  <PlayIcon size={28} />
                </PurpleFilledCircle>
              )}
            </Box>
          )}
          {(variant === "default" || variant === "webinar") &&
            Array.isArray(tagLabels) &&
            tagLabels.length > 0 && (
              <Flex sx={{ gap: "1.5rem", flexWrap: "wrap", mb: "1.5rem" }}>
                {tagLabels.slice(0, 2).map((label, index) => (
                  <Tag
                    key={`${label}-${index}`}
                    label={label}
                    bgColor={
                      Array.isArray(tagColors) && tagColors[index] != null
                        ? tagColors[index]
                        : colors.neutral200
                    }
                  />
                ))}
              </Flex>
            )}
          {variant === "webinar" && hasFreeTag && (
            <img src={freeTag} style={freeTagStyle} alt="Free tag" />
          )}
          {variant === "academy" && (
            <Flex sx={academyThumbnail}>
              {hasFreeTag && (
                <img src={freeTag} style={freeTagStyle} alt="Free tag" />
              )}
              <Text sx={titleTextStyle}>{titleText}</Text>
            </Flex>
          )}
          {variant === "default" && iconName && 
            <Box sx={iconStyle}>
              <DisplayIcon iconName={iconName} theme={theme} />
            </Box>
          }
          {topLabel && <Box sx={primaryLabelStyle}>{topLabel}</Box>}
          {title && (
            <Heading
              as="h3"
              sx={{
                ...titleStyle,
                color: titleColor ?? (hasUrl ? colors.primaryMain : colors.black),
              }}
            >
              {title}
            </Heading>
          )}
          {content && (
            <Box
              sx={{
                ...textStyle,
                color: contentColor ?? textStyle.color,
              }}
            >
              <Text>{content}</Text>
            </Box>
          )}
          {bottomLabel && <Box sx={secondaryLabelStyle}>{bottomLabel}</Box>}
          {buttonLabel && (
            <Button
              size="large"
              variant={btnVariant}
              btnWidth={btnWidth}
              onClick={onBtnClick}
              iconName={btnIcon}
              direction={btnIconDirection}
              customStyles={{ mt: "1.5rem" }}
            >
              {buttonLabel}
            </Button>
          )}
        </Flex>
  );
  return (
    <InfoTileWrapper sx={{
      border: hasBorder ? `1px solid ${colors.neutral200}` : "none",
      cursor: hasUrl ? "pointer" : "default",
      backgroundColor,
      "&:hover": {
        backgroundColor: hideHoverBgColor ? backgroundColor : colors.primary100,
      },
      maxWidth:"26rem",
      ...customStyles,
    }}>
      {hasUrl ? renderCardLink(url, contentBox) : contentBox}
    </InfoTileWrapper>
  );
};

InfoTile.defaultProps = {
  hasBorder: true,
  variant: "default",
  titleText: "Default Title text",
  imageAltText: "Info Tile Image",
  hasFreeTag: false,
  url: "",
  customStyles: {},
  hideHoverBgColor: false,
  showPlayButton: false,
  backgroundColor: colors.white,
  titleColor: undefined,
  contentColor: undefined,
};

InfoTile.propTypes = {
  image: PropTypes.object,
  imageUrl: PropTypes.string,
  imageAltText: PropTypes.string,
  iconName: PropTypes.string,
  theme: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  tagLabel: PropTypes.string,
  tagColor: PropTypes.string,
  tagLabels: PropTypes.arrayOf(PropTypes.string),
  tagColors: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.oneOf(["default", "academy", "webinar", "logo"]),
  btnWidth: PropTypes.oneOf(["default", "full"]),
  btnVariant: PropTypes.oneOf(["filled", "outlined"]),
  topLabel: PropTypes.node,
  bottomLabel: PropTypes.node,
  buttonLabel: PropTypes.string,
  onBtnClick: PropTypes.func,
  hasBorder: PropTypes.bool,
  hasFreeTag: PropTypes.bool,
  showPlayButton: PropTypes.bool,
  titleText: PropTypes.string,
  url: PropTypes.string,
  customStyles: PropTypes.object,
  backgroundColor: PropTypes.string,
  hideHoverBgColor: PropTypes.bool,
  btnIcon: PropTypes.string,
  btnIconDirection: PropTypes.string,
  titleColor: PropTypes.string,
  contentColor: PropTypes.string,
};

export default InfoTile;